import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  fetchAllData,
  fetchData,
  fetchDeleteData,
} from "../../helpers/externapi";
import Layout from "../../Layout/Layout";

function CommunityGroupsAdd() {
  const { Id: communityId } = useParams();
  const navigate = useNavigate();

  /* ------------------------------- state ---------------------------------- */
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [allGroups, setAllGroups] = useState([]);
  const [allHospitals, setAllHospitals] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableHospitals, setAvailableHospitals] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [assignedHospitals, setAssignedHospitals] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedHospitals, setSelectedHospitals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [toast, setToast] = useState({ show: false, msg: "", variant: "success" });

  /* ----------------------------- helpers ---------------------------------- */
  const showToast = (msg, variant = "success") => {
    setToast({ show: true, msg, variant });
    setTimeout(() => setToast({ ...toast, show: false }), 3000);
  };

  const resetSelections = () => {
    setSelectedGroups([]);
    setSelectedHospitals([]);
  };

  /* --------------------- load assigned / available ------------------------ */
  const refreshData = useCallback(
    async (commId, masterGroups, masterHospitals) => {
      if (!commId) return;
      try {
        setLoadingMessage("Loading assignments...");

        const [assignedHospRes, assignedGrpRes] = await Promise.all([
          fetchData(`lambdaAPI/CommunityHospitals/GetHospitals`, {
            communityId: commId,
            isActive: null, // Get all hospitals
          }),
          fetchData(`lambdaAPI/CommunityGroup/FetchGroupsByCommunityId`, {
            communityId: commId,
            isActive: null, // Get all groups
          }),
        ]);

        const assignedHospitalData = assignedHospRes ?? [];
        const assignedGroupData = assignedGrpRes ?? [];

        const assignedHospitalIds = new Set(assignedHospitalData.map((h) => h.HospitalId));
        const assignedGroupIds = new Set(assignedGroupData.map((g) => g.GroupId));

        // Set assigned items with full details
        setAssignedGroups(assignedGroupData.map(g => ({
          id: g.GroupId,
          name: g.GroupName || masterGroups.find(mg => mg.id === g.GroupId)?.name || 'Unknown Group',
          assignmentId: g.CommunityGroupId
        })));

        setAssignedHospitals(assignedHospitalData.map(h => ({
          id: h.HospitalId,
          name: h.HospitalName || masterHospitals.find(mh => mh.HospitalId === h.HospitalId)?.HospitalName || 'Unknown Hospital',
          assignmentId: h.CommunityHospitalId
        })));

        // Set available items (not assigned)
        setAvailableGroups(masterGroups.filter((g) => !assignedGroupIds.has(g.id)));
        setAvailableHospitals(masterHospitals.filter((h) => !assignedHospitalIds.has(h.HospitalId)));
      } catch (err) {
        console.error("Failed to load assignments", err);
        showToast("Could not load assignments", "danger");
      }
    },
    []
  );

  /* --------------------- initial master list load ------------------------- */
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        setLoadingMessage("Loading data...");

        const commRes = await fetchAllData(`lambdaAPI/Community/GetById/${communityId}`);
        setSelectedCommunity(commRes?.[0] ?? null);

        const [grpMasterRes, hospMasterRes] = await Promise.all([
          fetchData("lambdaAPI/Group/all", { skip: 0, take: 0 }),
          fetchData("Hospital/all", { skip: 0, take: 0, IsActive: true }),
        ]);

        const masterGroups = (grpMasterRes?.items ?? grpMasterRes ?? []).map((g) => ({
          id: g.GroupId ?? g.id,
          name: g.GroupName ? g.GroupName : g.Name,
        }));
        const masterHospitals = (hospMasterRes ?? []).map((h) => ({
          HospitalId: h.HospitalId ?? h.id,
          HospitalName: h.HospitalName ?? h.name,
        }));

        setAllGroups(masterGroups);
        setAllHospitals(masterHospitals);

        await refreshData(communityId, masterGroups, masterHospitals);
      } catch (err) {
        console.error("Initial load failed", err);
        showToast("Initial load failed", "danger");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [communityId, refreshData]);

  /* ------------------------- save assignments ---------------------------- */
  const handleSubmit = async () => {
    if (!selectedCommunity) return;
    if (selectedGroups.length === 0 && selectedHospitals.length === 0) return;

    try {
      setIsLoading(true);
      setLoadingMessage("Saving assignments...");

      await Promise.all([
        selectedHospitals.length
          ? fetchData("lambdaAPI/CommunityHospitals/add", {
            communityId: selectedCommunity.CommunityId ?? selectedCommunity.id,
            hospitalId: selectedHospitals,
          })
          : Promise.resolve({}),
        selectedGroups.length
          ? fetchData("lambdaAPI/CommunityGroup/add", {
            communityId: selectedCommunity.CommunityId ?? selectedCommunity.id,
            groupId: selectedGroups,
          })
          : Promise.resolve({}),
      ]);

      showToast("Assignments saved successfully!");
      resetSelections();
      await refreshData(communityId, allGroups, allHospitals);
    } catch (err) {
      console.error("Save error", err);
      showToast("Failed to save assignments", "danger");
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------- remove assignment ---------------------------- */
  const handleRemoveAssignment = async (type, assignmentId, itemName) => {
    if (!window.confirm(`Are you sure you want to remove "${itemName}"?`)) {
      return;
    }

    try {
      setIsLoading(true);
      setLoadingMessage(`Removing ${itemName}...`);

      const endpoint = type === 'group'
        ? `lambdaAPI/CommunityGroup/delete/${assignmentId}`
        : `lambdaAPI/CommunityHospitals/delete/${assignmentId}`;

      await fetchDeleteData(endpoint);
      showToast(`${itemName} removed successfully!`);
      await refreshData(communityId, allGroups, allHospitals);
    } catch (err) {
      console.error("Remove error", err);
      showToast(`Failed to remove ${itemName}`, "danger");
    } finally {
      setIsLoading(false);
    }
  };

  /* --------------------- react-select option arrays ----------------------- */
  const groupOptions = availableGroups.map((g) => ({ value: g.id, label: g.name }));
  const hospitalOptions = availableHospitals.map((h) => ({ value: h.HospitalId, label: h.HospitalName }));

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: '0.375rem',
      border: '1px solid #d1d5db',
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #6366f1',
      },
      minHeight: '42px',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#f3f4f6',
      borderRadius: '0.25rem',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#374151',
      fontWeight: 500,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#6b7280',
      '&:hover': {
        backgroundColor: '#e5e7eb',
        color: '#374151',
      },
    }),
  };

  /* ------------------------------ render ---------------------------------- */
  return (
    <Layout>
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <button
              className="btn btn-outline-secondary me-3"
              onClick={() => navigate("/communities")}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
            <div>
              <h4 className="mb-1">Community Management</h4>
              {selectedCommunity && (
                <p className="text-muted small mb-0">
                  {selectedCommunity.CommunityName ?? selectedCommunity.name}
                </p>
              )}
            </div>
          </div>

          {selectedCommunity && (
            <div className="d-flex gap-3">
              <span className="badge bg-primary px-3 py-2">
                Groups: {assignedGroups.length}
              </span>
              <span className="badge bg-info px-3 py-2">
                Hospitals: {assignedHospitals.length}
              </span>
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="d-flex align-items-center justify-content-center my-4">
            <div className="spinner-border text-primary me-3" role="status"></div>
            <span>{loadingMessage}</span>
          </div>
        )}

        {selectedCommunity ? (
          <div className="card">
            <div className="card-body">
              {/* Assignment Form */}
              <div className="mb-4">
                <div className="alert alert-info mb-4">
                  <i className="bi bi-info-circle me-2"></i>
                  Select groups and hospitals to assign to this community. Only unassigned items are shown.
                </div>

                <div className="row">
                  {/* Available Groups */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-people me-2"></i>
                      Available Groups ({groupOptions.length})
                    </label>
                    <Select
                      options={groupOptions}
                      isMulti
                      placeholder="Select groups..."
                      closeMenuOnSelect={false}
                      value={groupOptions.filter((o) => selectedGroups.includes(o.value))}
                      onChange={(opts) => setSelectedGroups(opts.map((o) => o.value))}
                      styles={selectStyles}
                      classNamePrefix="react-select"
                    />
                    {/* Already Assigned Groups */}
                    {assignedGroups.length > 0 && (
                      <div className="mt-3">
                        <label className="form-label fw-semibold text-muted" style={{ fontSize: '14px' }}>
                          Already Assigned Groups
                        </label>
                        <div className="d-flex flex-wrap gap-2">
                          {assignedGroups.map((group, index) => (
                            <span
                              key={index}
                              className="badge bg-primary d-flex align-items-center gap-2"
                              style={{ padding: '8px 12px', fontSize: '14px' }}
                            >
                              {group.name}
                              {/* <button
                                type="button"
                                className="btn-close btn-close-white"
                                aria-label="Remove"
                                onClick={() => handleRemoveAssignment('group', group.assignmentId, group.name)}
                                disabled={isLoading}
                              /> */}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Available Hospitals */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-hospital me-2"></i>
                      Available Hospitals ({hospitalOptions.length})
                    </label>
                    <Select
                      options={hospitalOptions}
                      isMulti
                      placeholder="Select hospitals..."
                      closeMenuOnSelect={false}
                      value={hospitalOptions.filter((o) => selectedHospitals.includes(o.value))}
                      onChange={(opts) => setSelectedHospitals(opts.map((o) => o.value))}
                      styles={selectStyles}
                      classNamePrefix="react-select"
                    />
                    {/* Already Assigned Hospitals */}
                    {assignedHospitals.length > 0 && (
                      <div className="mt-3">
                        <label className="form-label fw-semibold text-muted" style={{ fontSize: '14px' }}>
                          Already Assigned Hospitals
                        </label>
                        <div className="d-flex flex-wrap gap-2">
                          {assignedHospitals.map((hospital, index) => (
                            <span
                              key={index}
                              className="badge bg-info d-flex align-items-center gap-2"
                              style={{ padding: '8px 12px', fontSize: '14px' }}
                            >
                              {hospital.name}
                              {/* <button
                                type="button"
                                className="btn-close btn-close-white"
                                aria-label="Remove"
                                onClick={() => handleRemoveAssignment('hospital', hospital.assignmentId, hospital.name)}
                                disabled={isLoading}
                              /> */}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Selection Summary */}
                {(selectedGroups.length > 0 || selectedHospitals.length > 0) && (
                  <div className="alert alert-success mb-4">
                    <i className="bi bi-check-circle me-2"></i>
                    <strong>{selectedGroups.length + selectedHospitals.length} items selected:</strong>
                    <span className="ms-2">
                      {selectedGroups.length > 0 && `${selectedGroups.length} groups`}
                      {selectedGroups.length > 0 && selectedHospitals.length > 0 && ', '}
                      {selectedHospitals.length > 0 && `${selectedHospitals.length} hospitals`}
                    </span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={resetSelections}
                  >
                    Reset
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={
                      isLoading ||
                      (selectedGroups.length === 0 && selectedHospitals.length === 0)
                    }
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        Save Assignments ({selectedGroups.length + selectedHospitals.length})
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : !isLoading && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Community Not Found</strong>
            <br />
            The requested community could not be found.
          </div>
        )}

        {/* Toast */}
        {toast.show && (
          <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1080 }}>
            <div
              className={`alert alert-${toast.variant === 'success' ? 'success' : 'danger'} mb-0`}
              style={{ minWidth: '300px' }}
            >
              <i className={`bi ${toast.variant === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle'} me-2`}></i>
              {toast.msg}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default CommunityGroupsAdd;